import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuthentication, useApi } from './index';

import { AgentExtensionLogData } from '../services/api/types';

export interface AgentExtensionContextProps {
  agentExtensionLogsData: AgentExtensionLogData | null;
  setAgentExtensionLogsData: React.Dispatch<
    React.SetStateAction<AgentExtensionLogData | null>
  >;
}

export const AgentExtensionContext =
  createContext<AgentExtensionContextProps | null>(null);

export const AgentExtensionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { api } = useApi();
  const { auth } = useAuthentication();
  const [loading, setLoading] = useState(true);

  const [agentExtensionLogsData, setAgentExtensionLogsData] =
    useState<AgentExtensionLogData | null>(null);

  const isUserSignedIn = auth.isUserSignedIn();

  useEffect(() => {
    let isMounted = true;
    let extension_logs_response = {
      extension: '',
      check_in_time: '',
      check_out_time: '',
      release_time: '',
      first_name: '',
      last_name: '',
      email: '',
      duration: 0,
      modified_by: '',
      modified_date: '',
      can_release: false,
      can_modify: false,
    };
    const getAgentExtensionLogsData = async () => {
      const userEmail = auth.getUserEmail();

      if (isUserSignedIn && userEmail) {
        const agent_extension_logs =
          await api.get_agent_extension_logs(userEmail);

        if (Object.keys(agent_extension_logs).length > 0) {
          extension_logs_response = {
            extension: agent_extension_logs?.extension
              ? agent_extension_logs?.extension
              : '',
            check_out_time: agent_extension_logs?.check_out_time
              ? agent_extension_logs?.check_out_time
              : '',
            check_in_time: agent_extension_logs?.check_in_time
              ? agent_extension_logs?.check_in_time
              : '',
            release_time: agent_extension_logs?.release_time
              ? agent_extension_logs?.release_time
              : '',

            first_name: agent_extension_logs?.first_name
              ? agent_extension_logs?.first_name
              : '',
            last_name: agent_extension_logs?.last_name
              ? agent_extension_logs?.last_name
              : '',
            email: agent_extension_logs?.email
              ? agent_extension_logs?.email
              : '',
            duration: agent_extension_logs?.duration
              ? agent_extension_logs?.duration
              : 0,
            modified_by: agent_extension_logs?.modified_by
              ? agent_extension_logs?.modified_by
              : '',
            modified_date: agent_extension_logs?.modified_date
              ? agent_extension_logs?.modified_date
              : '',
            can_release: agent_extension_logs?.can_release
              ? agent_extension_logs?.can_release
              : false,
            can_modify: agent_extension_logs?.can_modify
              ? agent_extension_logs?.can_modify
              : false,
          };
        }
      }
      if (isMounted) {
        setAgentExtensionLogsData(extension_logs_response);
        setLoading(false);
      }
    };

    if (!isUserSignedIn) {
      setLoading(false);
    } else {
      if (!agentExtensionLogsData) {
        getAgentExtensionLogsData();
      } else {
        setLoading(false);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [agentExtensionLogsData, api, isUserSignedIn, auth]);

  if (loading) {
    return null;
  }

  return (
    <AgentExtensionContext.Provider
      value={{
        agentExtensionLogsData,
        setAgentExtensionLogsData,
      }}
    >
      {children}
    </AgentExtensionContext.Provider>
  );
};

export function useAgentExtension(): AgentExtensionContextProps {
  const context = useContext(AgentExtensionContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useAgentExtension outside of AgentExtensionProvider');
}
